import {
  ButtonGroup,
  Button as ChakraButton,
  Flex,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { useChartTheme } from '@revelio/replots';

import { dataBuilderStore } from '../data-builder.store';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import styles from './data-builder-footer.module.css';

type DataBuilderFooterProps = {
  actions?: ReactNode;
};

export const DataBuilderFooter = ({ actions }: DataBuilderFooterProps) => {
  const { previousPath, goToChooseStep } = useDataBuilderNavigation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isNewTheme } = useChartTheme();

  const backLink =
    previousPath !== undefined ? (
      <Link as={NavLink} to={previousPath} className={styles.back}>
        Back
      </Link>
    ) : null;

  const handleConfirmStartOver = () => {
    dataBuilderStore.getState().reset();
    goToChooseStep();
    onClose();
  };

  return (
    <Flex
      as="footer"
      className={styles.footer}
      align="center"
      justify="space-between"
      borderTopWidth="1px"
      borderTopStyle="solid"
      borderColor="gray.200"
      padding="12px 24px"
      minHeight="48px"
      flexShrink={0}
    >
      <Flex gap={3} align="center">
        <Popover
          placement="top-start"
          returnFocusOnClose={false}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
        >
          <PopoverTrigger>
            <Link
              as="button"
              type="button"
              className={styles.startOver}
              fontSize="sm"
              fontWeight="600"
              color="red"
              cursor="pointer"
            >
              Start over
            </Link>
          </PopoverTrigger>
          <PopoverContent className={styles.confirmPopover}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader color="text.primary" fontWeight="600">
              Confirm Action
            </PopoverHeader>
            <PopoverBody color="text.primary">
              Are you sure you want to start over? This will reset all your
              changes.
            </PopoverBody>
            <PopoverFooter display="flex" justifyContent="flex-end">
              <ButtonGroup size="sm">
                <ChakraButton
                  variant="outline"
                  colorScheme="gray"
                  color="text.primary"
                  onClick={onClose}
                >
                  Cancel
                </ChakraButton>
                <ChakraButton
                  data-testid="confirm-start-over"
                  colorScheme="red"
                  onClick={handleConfirmStartOver}
                >
                  Confirm
                </ChakraButton>
              </ButtonGroup>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
        {previousPath !== undefined && !isNewTheme && backLink}
      </Flex>
      <Flex gap={3} align="center">
        {previousPath !== undefined && isNewTheme && backLink}
        {actions}
      </Flex>
    </Flex>
  );
};
